// Photos used across the site
import { PHOTOS } from "./photos";

export const SITE = {
  name: "Recovery Connections of Central Florida",
  shortName: "Recovery Connections",
  helpline: "407.RECOVER",
  helplineDigits: "407.732.6837",
  helplineTel: "+14077326837",
  email: "info@myrecoveryconnections.org",
  address: "Central Florida",
  logoColor:
    "https://customer-assets.emergentagent.com/job_3f41b74e-be65-4bad-8a9f-66d725b4ab12/artifacts/dc2t3y0n_LOGO-300x300-1-200x200.png.webp",
  logoWhite:
    "https://customer-assets.emergentagent.com/job_3f41b74e-be65-4bad-8a9f-66d725b4ab12/artifacts/ip3u2qpt_White-logo-resized-150x150.webp",
};

// Multi-level navigation structure (mirrors live site)
export const NAV = [
  { label: "Home", href: "/" },
  {
    label: "About Us",
    href: "/about",
    columns: [
      {
        title: "About Us",
        items: [
          { label: "Our Mission", href: "/about", desc: "Our core values and what guides us." },
          { label: "Our Leadership & Staff", href: "/leadership", desc: "Meet our peer-led team." },
          { label: "Community Impact", href: "/community-impact", desc: "The reach of our work." },
          { label: "Blog", href: "/blog", desc: "Stories, insights and resources." },
        ],
      },
      {
        title: "Programs",
        items: [
          { label: "All Programs", href: "/programs", desc: "Overview of services we provide." },
          { label: "Recovery Support Services (RSS)", href: "/programs/rss", desc: "In-center peer support." },
          { label: "Mobile RSS", href: "/programs/mobile-rss", desc: "Meeting people where they are." },
          { label: "Justice Involved RSS", href: "/programs/justice-rss", desc: "Court-connected support." },
        ],
      },
    ],
    feature: {
      title: "Together We Recover",
      desc: "A 501(c)(3) Recovery Community Organization led by people in recovery.",
      image: PHOTOS.meeting4,
      cta: { label: "Read our story", href: "/about" },
    },
  },
  {
    label: "Need Help?",
    href: "/need-help",
    columns: [
      {
        title: "Get Support",
        items: [
          { label: "Seeking Recovery", href: "/need-help#seeking", desc: "Take the first step." },
          { label: "Sustaining Recovery", href: "/need-help#sustaining", desc: "Stay strong, stay connected." },
          { label: "For Families", href: "/need-help#families", desc: "Support for loved ones." },
          { label: "FAQ", href: "/need-help#faq", desc: "Common questions answered." },
        ],
      },
    ],
    feature: {
      title: "Talk to a Peer Specialist",
      desc: "Confidential, judgment-free support — available now.",
      image: PHOTOS.therapy3,
      cta: { label: "Call 407.RECOVER", href: "tel:+14077326837" },
    },
  },
  {
    label: "Ways to Help",
    href: "/ways-to-help",
    columns: [
      {
        title: "Get Involved",
        items: [
          { label: "Donate", href: "/donate", desc: "Power peer-led recovery." },
          { label: "Volunteer", href: "/ways-to-help#volunteer", desc: "Give time and lived experience." },
          { label: "Partner With Us", href: "/ways-to-help#partners", desc: "For organizations and providers." },
          { label: "Advocate", href: "/ways-to-help#advocate", desc: "Help end stigma." },
        ],
      },
    ],
    feature: {
      title: "Every Gift Multiplies",
      desc: "Your support funds peer specialists, training, and outreach across Central Florida.",
      image: PHOTOS.conversation,
      cta: { label: "Donate now", href: "/donate" },
    },
  },
  { label: "Contact", href: "/contact" },
];

export const CORE_VALUES = [
  { title: "Hope", description: "Believing that recovery IS possible.", icon: "Sparkles" },
  { title: "Respect", description: "Embracing and encouraging the person in recovery to direct their personal recovery plan.", icon: "HandHeart" },
  { title: "Advocacy", description: "Standing in the gap and promoting positive change for the recovery movement.", icon: "Megaphone" },
  { title: "Inclusion of Multiple Pathways", description: "Creating an atmosphere that cultivates support for individualized recovery journeys.", icon: "Compass" },
];

export const AUDIENCE = [
  { title: "Seeking Recovery", description: "You are taking the first courageous step. Connect with a Certified Recovery Peer Specialist who has walked a similar path.", cta: "Find Support", icon: "Footprints", anchor: "seeking" },
  { title: "Sustaining Recovery", description: "Stay connected to a vibrant community that helps you build resilience, accountability, and meaning every day.", cta: "Stay Connected", icon: "HeartHandshake", anchor: "sustaining" },
  { title: "Concerned About a Loved One", description: "You are not alone. We help families understand recovery, set healthy boundaries, and access resources together.", cta: "Family Resources", icon: "Users", anchor: "families" },
  { title: "Wanting to Support the Movement", description: "Volunteer, donate, or partner with us to expand peer-led recovery support across Central Florida.", cta: "Get Involved", icon: "HandHeart", anchor: "support" },
];

export const ABOUT_BLOCKS = [
  {
    id: "rco", eyebrow: "Who We Are", title: "A Recovery Community Organization",
    body: "A Recovery Community Organization (RCO) is an independent, non-profit led and governed by community members passionate about the recovery movement. To maintain a strong connection between our work and the people we serve, 51% of our Board of Directors self-identify as individuals in recovery.",
    cta: { label: "Meet our Leadership", href: "/leadership" }, image: PHOTOS.tableMeeting, align: "left",
  },
  {
    id: "crps", eyebrow: "Our People", title: "Certified Recovery Peer Specialists",
    body: "A Recovery Coach is a Certified Recovery Peer Specialist (CRPS) who self-identifies as a person in recovery. The CRPS credential identifies individuals who have used their lived experience and learned skills to help others achieve and sustain long-term recovery.",
    cta: { label: "Read about our Team", href: "/leadership" }, image: PHOTOS.diverseTeam, align: "right",
  },
  {
    id: "programs", eyebrow: "What We Do", title: "Programs Built on Lived Experience",
    body: "Our programs meet people where they are — whether at the very beginning of their journey or further along a sustainable path. They are the heart of our organization, made possible by our Certified Recovery Peer Specialists and the invaluable lived experience they bring. People truly make the programs.",
    cta: { label: "Explore Programs", href: "/programs" }, image: PHOTOS.groupCircle, align: "left",
  },
];

export const NEWS = [
  { title: "Overcoming Stigma: Embracing Medication-Assisted Treatment", excerpt: "In the realm of addiction treatment, the use of medication-assisted treatment (MAT) is reshaping outcomes — yet stigma remains a barrier worth confronting.", image: PHOTOS.handsHeart, date: "March 18, 2026", category: "Healthcare", slug: "overcoming-stigma-mat" },
  { title: "The Transformative Power of Recovery Peer Support", excerpt: "Peer support has emerged as a cornerstone of lasting sobriety. Discover how shared experience builds bridges to long-term wellness.", image: PHOTOS.handToSun, date: "February 11, 2026", category: "Peer Support", slug: "power-of-peer-support" },
  { title: "Understanding the Financial Impact of Addiction Treatment", excerpt: "Addiction is a complex health issue with real financial implications. Learn how recovery support services help families plan and heal.", image: PHOTOS.forestPath, date: "January 22, 2026", category: "Resources", slug: "financial-impact" },
  { title: "It Has Been a Dream That These People Are Helping People Just Like Me", excerpt: "Stories of lived experience from participants who found hope through community-led peer recovery.", image: PHOTOS.therapy3, date: "December 4, 2025", category: "Stories", slug: "dream-coming-true" },
  { title: "The Power of Lived Experience: A Journey Through Recovery", excerpt: "What it means to walk alongside someone who has been there — and the difference it makes in long-term outcomes.", image: PHOTOS.conversation, date: "November 12, 2025", category: "Peer Support", slug: "lived-experience" },
  { title: "Meeting People Where They Are: Mobile RSS in Action", excerpt: "How our Mobile Recovery Support Services break down barriers and bring care directly to neighborhoods that need it most.", image: PHOTOS.meeting4, date: "October 1, 2025", category: "Programs", slug: "mobile-rss-action" },
];

export const STATS = [
  { value: "1,200+", label: "Individuals supported" },
  { value: "15+", label: "Certified peer specialists" },
  { value: "24/7", label: "Helpline availability" },
  { value: "100%", label: "Peer-led, community-driven" },
];

export const FAQS = [
  { q: "Is your support free of charge?", a: "Yes. As a 501(c)(3) nonprofit Recovery Community Organization, our peer recovery support services are provided at no cost to participants and their families." },
  { q: "What is a Certified Recovery Peer Specialist (CRPS)?", a: "A CRPS is a credentialed professional who self-identifies as a person in recovery and has been trained to use their lived experience and learned skills to help others achieve and sustain recovery." },
  { q: "Do you offer support for families?", a: "Absolutely. We work with concerned family members and loved ones, providing education, resources, and recovery-informed guidance." },
  { q: "Are auxiliary aids available?", a: "Yes. For Peer Recovery Support Services, auxiliary aid and interpreter services for hearing-disabled and limited-English proficient persons are available at no cost to participants and their companions." },
  { q: "Where are you located?", a: "We serve Central Florida with Community Centers in Orange, Seminole, Brevard, and Osceola counties — plus a Mobile RSS team that travels to where people need us." },
  { q: "How can I become a peer specialist?", a: "Reach out to us. Our Director of Statewide Training oversees pathways into the CRPS credential, including coursework, mentorship and supervised practice." },
];
